// ─── Cross-promotion — SUITE-AWARE ─────────────────────────────────
//
// Rebuilt from the old 8-root-tool version (which was stale: knew none
// of the ~34 suite tools and used pre-/stack-migration paths). Now:
//
//   42 promotable tools, each tagged with its SUITE and a real
//   post-migration path (verified against App.jsx routes).
//
// Pick model per block (Ian-locked weighting):
//   75% — a SAME-SUITE sibling (highest in-task relevance)
//   15% — the SUITE ITSELF / parent ("More NerdChem", "NerdSuite has
//         6 suites") — the real multi-suite discovery lever
//    5% — a CROSS-SUITE tool, biased to a topically-RELATED suite
//   5% — a PURE-RANDOM tool from anywhere (serendipity / anti-filter-
//         bubble; keeps the related-suite bias honest)
//
// Sibling COUNT per block: 40% show 1 · 50% show 2 · 10% show 3.
//
// Number policy (locked): copy may reference the 6-suite breadth but
// NEVER a tool count (always growing → re-rots).
//
// Not-live tools (quizcard, clearlens) keep "launching soon" in their
// phrases and a low weight so cross-promo never sells a dead route.

// suite key → display + the suite's own landing route (for the 15%
// "promote the suite itself" bucket).
export const SUITES = {
  stack: { name: 'NerdStack', path: '/stack' },
  chem:  { name: 'NerdChem',  path: '/chem'  },
  math:  { name: 'NerdMath',  path: '/math'  },
  phys:  { name: 'NerdPhys',  path: '/phys'  },
  tools: { name: 'NerdTools', path: '/tools' },
  bio:   { name: 'NerdBio',   path: '/bio'   },
};

// Topically-related suites for the 5% related-cross bucket. Asymmetry
// is fine; lookups are per-source. Chem↔Phys & Math↔Phys are the
// strongest student-context overlaps; Bio is more standalone.
export const RELATED = {
  stack: ['math', 'tools'],
  chem:  ['phys', 'math'],
  math:  ['phys', 'chem'],
  phys:  ['math', 'chem'],
  tools: ['stack', 'phys'],
  bio:   ['chem'],
};

// weight: 8 = live free tool · 3 = not-live (paid AI, launching soon).
export const TOOLS = {
  // ── NerdStack root ──────────────────────────────────────────────
  gradecalc:   { name: 'GradeCalc',    suite: 'stack', path: '/stack/gradecalc',  weight: 8, phrases: ['to figure out your GPA', 'for semester grade planning'] },
  timer:       { name: 'StudyTimer',   suite: 'stack', path: '/stack/timer',      weight: 8, phrases: ['to time your focus sessions', 'for Pomodoro studying'] },
  wordcount:   { name: 'WordCount',    suite: 'stack', path: '/stack/wordcount',  weight: 8, phrases: ['to check essay length', 'for reading-time estimates'] },
  percent:     { name: 'PercentCalc',  suite: 'stack', path: '/stack/percent',    weight: 8, phrases: ['to calculate percentages', 'for tip and discount math'] },
  convert:     { name: 'UnitConvert',  suite: 'stack', path: '/stack/convert',    weight: 8, phrases: ['to convert units fast', 'for chemistry and physics units'] },
  average:     { name: 'AverageCalc',  suite: 'stack', path: '/stack/average',    weight: 8, phrases: ['to crunch averages', 'for mean, median, and standard deviation'] },
  quizcard:    { name: 'QuizCard',     suite: 'stack', path: '/stack/quizcard',   weight: 3, phrases: ['to turn notes into flashcards (launching soon)', 'for AI-generated quizzes (soon)'] },
  clearlens:   { name: 'ClearLens',    suite: 'stack', path: '/stack/clearlens',  weight: 3, phrases: ['to simplify tough concepts (launching soon)', 'for plain-language explanations (soon)'] },

  // ── NerdChem ────────────────────────────────────────────────────
  chem_periodic:  { name: 'Periodic Table',      suite: 'chem', path: '/chem/periodic-table',    weight: 8, phrases: ['to explore all 118 elements', 'for element properties at a tap'] },
  chem_molweight: { name: 'Molecular Weight',    suite: 'chem', path: '/chem/molecular-weight',  weight: 8, phrases: ['to compute molar mass', 'for molecular weight from a formula'] },
  chem_mole:      { name: 'Mole Calculator',     suite: 'chem', path: '/chem/mole-calculator',   weight: 8, phrases: ['to convert grams, moles, and molecules', 'for mole calculations'] },
  chem_ph:        { name: 'pH Calculator',       suite: 'chem', path: '/chem/ph-calculator',     weight: 8, phrases: ['to find pH and pOH', 'for acid-base concentration math'] },
  chem_dilution:  { name: 'Dilution Calculator', suite: 'chem', path: '/chem/dilution',          weight: 8, phrases: ['to solve dilution problems', 'for C1V1 = C2V2 any unknown'] },
  chem_sigfigs:   { name: 'Significant Figures', suite: 'chem', path: '/chem/sig-figs',          weight: 8, phrases: ['to count significant figures', 'for sig-fig rounding'] },
  chem_balancer:  { name: 'Equation Balancer',   suite: 'chem', path: '/chem/equation-balancer', weight: 8, phrases: ['to balance chemical equations', 'for exact whole-number coefficients'] },
  chem_gas:       { name: 'Gas Law Solver',      suite: 'chem', path: '/chem/gas-laws',          weight: 8, phrases: ['to solve PV = nRT', 'for any gas-law unknown'] },
  chem_stoich:    { name: 'Stoichiometry',       suite: 'chem', path: '/chem/stoichiometry',     weight: 8, phrases: ['to find the limiting reagent', 'for theoretical yield'] },

  // ── NerdMath ────────────────────────────────────────────────────
  math_calc:    { name: 'Scientific Calculator', suite: 'math', path: '/math/calculator', weight: 8, phrases: ['to crunch a quick expression', 'for trig, logs, and powers'] },
  math_quad:    { name: 'Quadratic Solver',      suite: 'math', path: '/math/quadratic',  weight: 8, phrases: ['to solve quadratics with full steps', 'for the quadratic formula worked out'] },
  math_trig:    { name: 'Trig & Unit Circle',    suite: 'math', path: '/math/trig',       weight: 8, phrases: ['to look up exact trig values', 'for the unit circle'] },
  math_log:     { name: 'Logarithm',             suite: 'math', path: '/math/logarithm',  weight: 8, phrases: ['to compute logs in any base', 'for natural and common logs'] },
  math_matrix:  { name: 'Matrix',                suite: 'math', path: '/math/matrix',     weight: 8, phrases: ['to solve a linear system', 'for determinant and inverse'] },

  // ── NerdPhys ────────────────────────────────────────────────────
  phys_proj:   { name: 'Projectile Motion',     suite: 'phys', path: '/phys/projectile', weight: 8, phrases: ['to solve projectile motion', 'for range, apex, and flight time'] },
  phys_kin:    { name: 'Kinematics (SUVAT)',    suite: 'phys', path: '/phys/kinematics', weight: 8, phrases: ['to solve SUVAT problems', 'for constant-acceleration motion'] },
  phys_force:  { name: "Newton's 2nd Law",      suite: 'phys', path: '/phys/force',      weight: 8, phrases: ['to solve F = ma', 'for force, mass, or acceleration'] },
  phys_energy: { name: 'Energy & Work',         suite: 'phys', path: '/phys/energy',     weight: 8, phrases: ['to compute kinetic and potential energy', 'for work done by a force'] },
  phys_ohms:   { name: "Ohm's Law",             suite: 'phys', path: '/phys/ohms',       weight: 8, phrases: ['to solve V = IR', 'for voltage, current, resistance, and power'] },
  phys_part:   { name: 'Particle Reference',    suite: 'phys', path: '/phys/particles',  weight: 8, phrases: ['to look up Standard Model particles', 'for particle properties reference'] },
  phys_wave:   { name: 'Wave Calculator',       suite: 'phys', path: '/phys/waves',      weight: 8, phrases: ['to solve v = fλ', 'for wave speed, frequency, and period'] },
  phys_mom:    { name: 'Momentum & Collisions', suite: 'phys', path: '/phys/momentum',   weight: 8, phrases: ['to solve 1D collisions', 'for momentum and restitution'] },

  // ── NerdTools ───────────────────────────────────────────────────
  tools_convert: { name: 'Unit Converter', suite: 'tools', path: '/tools/converter', weight: 8, phrases: ['to convert any unit', 'for length, mass, volume, and more'] },
  tools_level:   { name: 'Bubble Level',   suite: 'tools', path: '/tools/level',     weight: 8, phrases: ["to check if something's level", 'for a phone spirit level'] },
  tools_compass: { name: 'Compass',        suite: 'tools', path: '/tools/compass',   weight: 8, phrases: ['to find your heading', 'for a quick compass'] },
  tools_db:      { name: 'Decibel Meter',  suite: 'tools', path: '/tools/decibel',   weight: 8, phrases: ['to gauge how loud it is', 'for relative sound levels'] },
  tools_ruler:   { name: 'Screen Ruler',   suite: 'tools', path: '/tools/ruler',     weight: 8, phrases: ['to measure small objects', 'for a calibrated on-screen ruler'] },
  tools_qr:      { name: 'QR Code',        suite: 'tools', path: '/tools/qr',        weight: 8, phrases: ['to generate a QR code', 'for turning a link into a QR'] },

  // ── NerdBio ─────────────────────────────────────────────────────
  bio_dna:     { name: 'DNA / RNA Toolkit', suite: 'bio', path: '/bio/dna',           weight: 8, phrases: ['to transcribe and complement DNA', 'for GC content and reverse complement'] },
  bio_codon:   { name: 'Codon Translator',  suite: 'bio', path: '/bio/translate',     weight: 8, phrases: ['to translate DNA into protein', 'for the standard genetic code'] },
  bio_punnett: { name: 'Punnett Square',    suite: 'bio', path: '/bio/punnett',       weight: 8, phrases: ['to run a Punnett square', 'for genotype and phenotype ratios'] },
  bio_hw:      { name: 'Hardy-Weinberg',    suite: 'bio', path: '/bio/hardy-weinberg', weight: 8, phrases: ['to compute Hardy-Weinberg frequencies', 'for allele and genotype frequencies'] },
  bio_lab:     { name: 'Lab Solutions',     suite: 'bio', path: '/bio/lab',           weight: 8, phrases: ['to calculate molarity', 'for C1V1 = C2V2 dilution'] },
  bio_anat:    { name: 'Anatomy Reference', suite: 'bio', path: '/bio/anatomy',       weight: 8, phrases: ['to drill anatomy with the quiz', 'for an anatomy study reference'] },
};

// Titles. Generic ones work anywhere; suite-self picks get their own
// title built dynamically (see getCrossPromo).
const TITLES = [
  'You might also like',
  'While you\'re here',
  'More free tools',
  'Another tool for this',
  'Keep going',
  'Also useful',
];

function pickWeighted(items) {
  const total = items.reduce((s, i) => s + i.weight, 0);
  let r = Math.random() * total;
  for (const i of items) { r -= i.weight; if (r <= 0) return i; }
  return items[items.length - 1];
}

const entries = () => Object.entries(TOOLS).map(([slug, d]) => ({ slug, ...d }));

// One pick honoring the 75/15/5/5 bucket model. `currentSlug` excluded.
// Returns either a tool {slug,...} or a suite-self {suiteSelf:true,...}.
function pickOne(currentSlug, currentSuite, exclude) {
  const r = Math.random();
  const all = entries().filter((t) => t.slug !== currentSlug && !exclude.has(t.slug));

  // 15% — promote the suite itself / parent.
  if (r >= 0.75 && r < 0.90) {
    // mostly the current suite ("More NerdChem"), sometimes parent NerdSuite.
    if (Math.random() < 0.7 && SUITES[currentSuite]) {
      const s = SUITES[currentSuite];
      return { suiteSelf: true, name: s.name, path: s.path };
    }
    return { suiteSelf: true, name: 'NerdSuite', path: '/', parent: true };
  }

  let pool;
  if (r < 0.75) {
    // 75% — same-suite sibling.
    pool = all.filter((t) => t.suite === currentSuite);
    if (pool.length === 0) pool = all; // tiny suite fallback
  } else if (r < 0.95) {
    // 5% — cross-suite, biased to a related suite.
    const rel = RELATED[currentSuite] || [];
    pool = all.filter((t) => rel.includes(t.suite));
    if (pool.length === 0) pool = all.filter((t) => t.suite !== currentSuite);
    if (pool.length === 0) pool = all;
  } else {
    // 5% — pure random anywhere (serendipity).
    pool = all;
  }
  return pickWeighted(pool);
}

function pickCount() {
  const r = Math.random();
  if (r < 0.40) return 1;   // 40% one
  if (r < 0.90) return 2;   // 50% two
  return 3;                 // 10% three
}

function sentence(items) {
  const part = (it) => it.suiteSelf
    ? (it.parent ? `${it.name} — every suite in one place` : `more ${it.name} tools`)
    : `${it.name} ${it.phrases[Math.floor(Math.random() * it.phrases.length)]}`;
  const p = items.map(part);
  if (p.length === 1) return `Try ${p[0]}.`;
  if (p.length === 2) return `Try ${p[0]} or ${p[1]}.`;
  return `Try ${p[0]}, ${p[1]}, or ${p[2]}.`;
}

/**
 * Fresh cross-promo for the given tool slug.
 * Returns { title, sentence, siblings:[{slug,name,path,...}] }.
 * Call once per page load (useMemo, empty deps).
 *
 * Back-compat: old call sites pass the old root slug (e.g. 'gradecalc')
 * — still valid keys here, so they keep working.
 */
export function getCrossPromo(currentSlug) {
  const cur = TOOLS[currentSlug];
  const currentSuite = cur ? cur.suite : 'stack';
  const count = pickCount();
  const chosen = [];
  const exclude = new Set();
  let guard = 0;
  while (chosen.length < count && guard++ < 40) {
    const pick = pickOne(currentSlug, currentSuite, exclude);
    if (pick.suiteSelf) {
      // at most one suite-self per block, and not if already chosen
      if (chosen.some((c) => c.suiteSelf)) continue;
      chosen.push(pick);
    } else {
      if (exclude.has(pick.slug)) continue;
      exclude.add(pick.slug);
      chosen.push(pick);
    }
  }
  const title = TITLES[Math.floor(Math.random() * TITLES.length)];
  return { title, sentence: sentence(chosen), siblings: chosen };
}
